import { Chord, Note, Scale } from "@tonaljs/tonal";
import * as Tone from "tone";
import { ensureSampler, playMidi, releaseActiveSampler, soundfontConfig } from "../js/music/soundfonts.js";
import {
  DEFAULT_CHORD_PLAY_STYLE,
  chordPlayStyleOptions,
  getChordPlayStyle,
  normalizeChordPlayStyle,
  noteIndexForPlayStyle,
  stepToBeatOffset,
} from "../playstyles.js";

const PPQ = 960;
const DEFAULT_SONG_BARS = 4;
const DEFAULT_SONG_TITLE = "Untitled Guitar Progression";
const STORAGE_KEY = "bardsCorner.guitar.v1";
const CHORD_PROGRESSIONS_STORAGE_KEY = "bardsCorner.chordProgressions.v1";
const DEFAULT_CHORD_SUSTAIN = 1;
const GUITAR_TUNING_MIDI = [40, 45, 50, 55, 59, 64];
const GUITAR_PITCHES = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];
const keys = ["C", "Db", "D", "Eb", "E", "F", "Gb", "G", "Ab", "A", "Bb", "B"];
const chromaticRoots = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];
const chordColors = ["#c73528", "#c7771f", "#a3a226", "#4fad27", "#2d92a8", "#8c22b4", "#b821b8"];
const nonDiatonicChordColor = "#6f7680";
const MOBILE_SCROLL_QUERY = "(max-width: 767.98px)";
const els = {
  chordEditModal: document.getElementById("chordEditModal"),
  chordEditRootSelect: document.getElementById("chordEditRootSelect"),
  chordEditTypeSelect: document.getElementById("chordEditTypeSelect"),
  confirmSaveSongButton: document.getElementById("confirmSaveSongButton"),
  diatonicChordList: document.getElementById("diatonicChordList"),
  diatonicKeySelect: document.getElementById("diatonicKeySelect"),
  diatonicModeSelect: document.getElementById("diatonicModeSelect"),
  fileLoadButton: document.getElementById("fileLoadButton"),
  fileNewButton: document.getElementById("fileNewButton"),
  fileSaveAsButton: document.getElementById("fileSaveAsButton"),
  fileSaveButton: document.getElementById("fileSaveButton"),
  keyButtonGrid: document.getElementById("keyButtonGrid"),
  keyDisplay: document.getElementById("keyDisplay"),
  keyModal: document.getElementById("keyModal"),
  loadSongList: document.getElementById("loadSongList"),
  loadSongMessage: document.getElementById("loadSongMessage"),
  loadSongModal: document.getElementById("loadSongModal"),
  loadSongOwnedTab: document.getElementById("loadSongOwnedTab"),
  loadSongOwnerSortButton: document.getElementById("loadSongOwnerSortButton"),
  loadSongSharedTab: document.getElementById("loadSongSharedTab"),
  loopButton: document.getElementById("loopButton"),
  metronomeButton: document.getElementById("metronomeButton"),
  meterSelect: document.getElementById("meterSelect"),
  originBardIndicator: document.getElementById("originBardIndicator"),
  playButton: document.getElementById("playButton"),
  progressionChordList: document.getElementById("progressionChordList"),
  saveExistingSongList: document.getElementById("saveExistingSongList"),
  saveSongDuplicateMessage: document.getElementById("saveSongDuplicateMessage"),
  saveSongMessage: document.getElementById("saveSongMessage"),
  saveSongModal: document.getElementById("saveSongModal"),
  saveSongNameInput: document.getElementById("saveSongNameInput"),
  saveSongSharedCheckbox: document.getElementById("saveSongSharedCheckbox"),
  songTitleInput: document.getElementById("songTitleInput"),
  stopButton: document.getElementById("stopButton"),
  sustainRange: document.getElementById("sustainRange"),
  sustainValue: document.getElementById("sustainValue"),
  tapTempoButton: document.getElementById("tapTempoButton"),
  tempoButton: document.getElementById("tempoButton"),
  tempoDisplay: document.getElementById("tempoDisplay"),
  tempoModal: document.getElementById("tempoModal"),
  transportSettingsButton: document.getElementById("transportSettingsButton"),
  transportModal: document.getElementById("transportModal"),
  transposeButton: document.getElementById("transposeButton"),
  transposeModal: document.getElementById("transposeModal"),
  unsavedChangesIndicator: document.getElementById("unsavedChangesIndicator"),
};

const state = {
  song: createDefaultSong(),
  selectedEventId: "",
  paletteAppendMode: true,
  defaultChordPlayStyle: DEFAULT_CHORD_PLAY_STYLE,
  chordSustain: DEFAULT_CHORD_SUSTAIN,
  loop: true,
  metronome: false,
  playing: false,
  playTimers: [],
  playbackGeneration: 0,
  previewGeneration: 0,
  tapTempoTimes: [],
  serverSongId: null,
  serverSongSignature: null,
  serverSongLocked: false,
  loadedOriginBardName: "",
  drumSampler: null,
  drumSamplerReady: false,
  drumSamplerLoadPromise: null,
  saveModalSongs: [],
  saveModalSelectedSongId: null,
  savingSong: false,
  loadingSaveSongs: false,
  loadModalSongs: [],
  loadModalMode: "owned",
  loadModalSortKey: "updatedAt",
  loadModalSortDirection: "desc",
  metronomeSynth: null,
  diatonicPreviewRangeOffset: 0,
  editingChordId: "",
};

function createDefaultSong() {
  const now = new Date().toISOString();
  const sectionDuration = barsToTicks(DEFAULT_SONG_BARS, 4, 4);
  const track = makeTrack("track_chords", "Chords", "chords", "acoustic_guitar_nylon");

  return {
    schema: "ChordSongJSON",
    schemaVersion: "0.1.0",
    id: makeId("song"),
    title: DEFAULT_SONG_TITLE,
    createdAt: now,
    updatedAt: now,
    ppq: PPQ,
    metadata: { composer: "", lyricist: "", notes: "" },
    timeline: {
      defaultTempoBpm: 120,
      tempoEvents: [{ tick: 0, bpm: 120 }],
      timeSignatureEvents: [{ tick: 0, numerator: 4, denominator: 4 }],
      keySignatureEvents: [{ tick: 0, key: "C", mode: "major" }],
    },
    form: {
      sections: [{ id: makeId("sec"), name: "Verse 1", startTick: 0, durationTicks: sectionDuration }],
      playOrder: [],
    },
    tracks: [track],
    markers: [{ id: makeId("marker"), tick: 0, name: "Verse 1", markerType: "section" }],
  };
}

function makeTrack(id, name, type, instrumentName) {
  return {
    id,
    name,
    type,
    instrumentName,
    muted: false,
    solo: false,
    volume: 1,
    pan: 0,
    octaveShift: 0,
    source: { originalTempoBpm: 120, originalPpq: PPQ, importedFrom: "user" },
    events: [],
  };
}

function createChordEvent(tick, durationTicks, symbol, playStyle = DEFAULT_CHORD_PLAY_STYLE) {
  const details = parseChord(symbol);
  return {
    id: makeId("chord"),
    eventType: "chord",
    tick,
    durationTicks,
    symbol: details.symbol,
    root: details.root,
    ...(details.bass ? { bass: details.bass } : {}),
    quality: details.quality,
    extensions: details.extensions,
    voicing: details.voicing,
    playStyle: normalizeChordPlayStyle(playStyle),
    guitarVoicingIndex: 0,
  };
}

function makeId(prefix) {
  if (crypto.randomUUID) {
    return `${prefix}_${crypto.randomUUID().slice(0, 8)}`;
  }
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

function ticksPerBeat(denominator = getMeter().denominator) {
  return PPQ * (4 / denominator);
}

function ticksPerBar(meter = getMeter()) {
  return meter.numerator * ticksPerBeat(meter.denominator);
}

function barsToTicks(bars, numerator, denominator) {
  return bars * numerator * ticksPerBeat(denominator);
}

function getMeter() {
  return state.song.timeline.timeSignatureEvents[0] || { tick: 0, numerator: 4, denominator: 4 };
}

function getTempo() {
  return state.song.timeline.tempoEvents[0]?.bpm || state.song.timeline.defaultTempoBpm || 120;
}

function getKeySignature() {
  return state.song.timeline.keySignatureEvents?.[0] || { tick: 0, key: "C", mode: "major" };
}

function getChordTrack() {
  let track = state.song.tracks.find((candidate) => candidate.type === "chords" || candidate.id === "track_chords");
  if (!track) {
    track = makeTrack("track_chords", "Chords", "chords", "acoustic_guitar_nylon");
    state.song.tracks.unshift(track);
  }
  return track;
}

function chordEvents() {
  return getChordTrack().events
    .filter((event) => event.eventType === "chord")
    .sort((first, second) => first.tick - second.tick);
}

function parseChord(symbol) {
  const trimmed = String(symbol || "C").trim() || "C";
  const chord = Chord.get(trimmed);
  const notes = chord.notes.length ? chord.notes : fallbackChordNotes(trimmed);
  const root = chord.tonic || notes[0]?.replace(/\d/g, "") || "C";
  const voicing = midiVoicing(notes, root);
  const aliases = Array.isArray(chord.aliases) ? chord.aliases.filter(Boolean) : [];
  return {
    symbol: trimmed,
    root,
    bass: chord.bass || "",
    quality: aliases[0] || inferQuality(trimmed),
    extensions: aliases.slice(1, 4),
    voicing,
  };
}

function fallbackChordNotes(symbol) {
  const root = symbol.match(/[A-G](?:#|b)?/)?.[0] || "C";
  const minor = /m(?!aj)/.test(symbol);
  return [root, Note.transpose(root, minor ? "3m" : "3M"), Note.transpose(root, "5P")];
}

function inferQuality(symbol) {
  if (symbol.includes("dim")) {
    return "dim";
  }
  if (/m(?!aj)/.test(symbol)) {
    return "minor";
  }
  return "major";
}

function midiVoicing(notes, root) {
  const rootMidi = Note.midi(`${root}4`) || 60;
  let previous = rootMidi - 1;
  return notes.map((note) => {
    let octave = 4;
    let midi = Note.midi(`${note}${octave}`);
    while (Number.isFinite(midi) && midi <= previous) {
      octave += 1;
      midi = Note.midi(`${note}${octave}`);
    }
    previous = Number.isFinite(midi) ? midi : previous + 4;
    return Number.isFinite(midi) ? midi : previous;
  });
}

function getScaleChords() {
  const keySignature = getKeySignature();
  const mode = keySignature.mode === "minor" ? "minor" : "major";
  const roots = Scale.get(`${keySignature.key} ${mode}`).notes;
  const fallbackRoots = Scale.get(`${keySignature.key} major`).notes;
  const qualities = mode === "minor"
    ? [
        { roman: "i", suffix: "m" },
        { roman: "ii dim", suffix: "dim" },
        { roman: "III", suffix: "" },
        { roman: "iv", suffix: "m" },
        { roman: "v", suffix: "m" },
        { roman: "VI", suffix: "" },
        { roman: "VII", suffix: "" },
      ]
    : [
        { roman: "I", suffix: "" },
        { roman: "ii", suffix: "m" },
        { roman: "iii", suffix: "m" },
        { roman: "IV", suffix: "" },
        { roman: "V", suffix: "" },
        { roman: "vi", suffix: "m" },
        { roman: "vii dim", suffix: "dim" },
      ];

  return (roots.length ? roots : fallbackRoots).map((root, index) => {
    const symbol = `${root}${qualities[index].suffix}`;
    return {
      roman: qualities[index].roman,
      symbol,
      notes: diatonicPreviewNotes(symbol),
      color: chordColors[index],
    };
  });
}

function diatonicPreviewNotes(symbol) {
  return diatonicPreviewMidis(symbol).map(midiNoteName);
}

function diatonicPreviewMidis(symbol) {
  const chord = Chord.get(symbol);
  const noteNames = chord.notes.length ? chord.notes : fallbackChordNotes(symbol);
  const octave = diatonicPreviewOctave();
  let previousMidi = (Note.midi(`${noteNames[0] || "C"}${octave}`) || 48) - 1;
  return noteNames.map((note) => {
    let noteOctave = octave;
    let midi = Note.midi(`${note}${noteOctave}`);
    while (Number.isFinite(midi) && midi <= previousMidi) {
      noteOctave += 1;
      midi = Note.midi(`${note}${noteOctave}`);
    }
    previousMidi = Number.isFinite(midi) ? midi : previousMidi + 4;
    return previousMidi;
  });
}

function formatRomanNumeral(roman) {
  return String(roman || "").replace(/\s+dim\b/i, "\u00b0");
}

function diatonicPreviewRangeLabel() {
  const keySignature = getKeySignature();
  const mode = keySignature.mode === "minor" ? "minor" : "major";
  const roots = Scale.get(`${keySignature.key} ${mode}`).notes;
  const fallbackRoots = Scale.get(`${keySignature.key} major`).notes;
  const scaleRoots = roots.length ? roots : fallbackRoots;
  const low = scaleRoots[0] || keySignature.key || "C";
  const high = scaleRoots[3] || Note.transpose(low, "4P") || "F";
  const octave = diatonicPreviewOctave();
  const lowMidi = Note.midi(`${low}${octave}`) || 48;
  const highMidi = Note.midi(`${high}${octave}`) || 53;
  return `${midiNoteName(lowMidi)}-${midiNoteName(highMidi)}`;
}

function diatonicPreviewOctave() {
  return Math.max(1, Math.min(6, 3 + (Number(state.diatonicPreviewRangeOffset) || 0)));
}

function midiNoteName(midi) {
  return Tone.Frequency(midi, "midi").toNote();
}

function shiftDiatonicPreviewRange(delta) {
  state.diatonicPreviewRangeOffset = Math.max(-2, Math.min(3, (Number(state.diatonicPreviewRangeOffset) || 0) + delta));
  renderDiatonicChords();
}

function romanForSymbol(symbol) {
  return getScaleChords().find((chord) => chord.symbol === symbol)?.roman || "";
}

function colorForChordSymbol(symbol) {
  return getScaleChords().find((chord) => chord.symbol === symbol)?.color || nonDiatonicChordColor;
}

function isDiatonicChordSymbol(symbol) {
  const scaleChromas = new Set(Scale.get(`${getKeySignature().key} ${getKeySignature().mode}`).notes.map((note) => Note.chroma(note)));
  const chord = Chord.get(String(symbol || ""));
  const notes = chord.notes.length ? chord.notes : fallbackChordNotes(symbol);
  return notes.map((note) => Note.chroma(note)).filter(Number.isFinite).every((chroma) => scaleChromas.has(chroma));
}

function guitarFingeringData() {
  return globalThis.GUITAR_FINGERING_DATA || null;
}

function normalizeGuitarRoot(root) {
  const chroma = Note.get(root || "").chroma;
  return Number.isFinite(chroma) ? GUITAR_PITCHES[chroma] : null;
}

function guitarChordToken(symbol) {
  const raw = String(symbol || "").trim();
  const rootMatch = raw.match(/^([A-G](?:#{1,2}|b{1,2})?)/);
  const suffix = rootMatch ? raw.slice(rootMatch[0].length).replace(/\s+/g, "") : raw;
  if (!suffix) {
    return "Major";
  }
  return { m: "Minor", min: "Minor", maj: "Major", "\u00b0": "dim" }[suffix] || suffix;
}

function guitarVoicingsForSymbol(symbol) {
  const data = guitarFingeringData();
  const rootMatch = String(symbol || "").trim().match(/^([A-G](?:#{1,2}|b{1,2})?)/);
  const root = normalizeGuitarRoot(rootMatch?.[1]);
  const chordToken = guitarChordToken(symbol);
  const rootIndex = data?.roots?.indexOf(root) ?? -1;
  const chordIndex = data?.chords?.indexOf(chordToken) ?? -1;
  const results = data?.voicings?.[String(rootIndex)]?.[String(chordIndex)]?.results;
  if (!Array.isArray(results)) {
    return [];
  }

  return results.map((result, resultIndex) => {
    const positions = [];
    (result.strings || []).forEach((stringSlots, stringIdx) => {
      const slot = Array.isArray(stringSlots) ? stringSlots[0] : null;
      if (!slot || slot.isMuted) {
        return;
      }
      const string = (result.strings.length - 1) - stringIdx;
      const fret = Number(slot.fretNo) || 0;
      positions.push({
        string,
        fret,
        midi: GUITAR_TUNING_MIDI[string] + fret,
        symbol: slot.symbol ?? "",
        isRootNote: Boolean(slot.isRootNote),
        barre: normalizeBarreValue(slot.bar),
        isBarre: Boolean(slot.bar),
      });
    });
    const frettedPositions = positions.filter((position) => position.fret > 0);
    const minFret = Math.min(...frettedPositions.map((position) => position.fret));
    const originalFirstFret = Number.isFinite(result.firstFret) ? result.firstFret : 1;
    const firstFret = originalFirstFret > 1 && Number.isFinite(minFret) ? minFret : originalFirstFret;
    return {
      positions,
      firstFret,
      label: resultIndex === 0 ? "Primary" : `Variation ${resultIndex + 1}`,
      variation: result.variation ?? resultIndex + 1,
    };
  }).filter((voicing) => voicing.positions.length);
}

function selectedGuitarVoicingForEvent(event) {
  const voicings = guitarVoicingsForSymbol(event?.symbol);
  if (!voicings.length) {
    return { voicings, voicing: null, index: 0 };
  }
  const index = Math.max(0, Math.min(Number(event.guitarVoicingIndex) || 0, voicings.length - 1));
  return { voicings, voicing: voicings[index], index };
}

function eventMidiNotes(event) {
  const { voicing } = selectedGuitarVoicingForEvent(event);
  const guitarNotes = voicing?.positions
    ?.slice()
    .sort((first, second) => (first.string - second.string) || (first.fret - second.fret))
    .map((position) => position.midi) || [];
  return guitarNotes.length ? guitarNotes : event.voicing || parseChord(event.symbol).voicing;
}

function render() {
  renderControls();
  renderDiatonicChords();
  renderProgression();
  renderFileMenuState();
}

function renderControls() {
  const tempoText = `${getTempo()} BPM`;
  const keySignature = getKeySignature();
  const keyText = `${keySignature.key} ${keySignature.mode === "minor" ? "minor" : "major"}`;
  els.songTitleInput.textContent = state.song.title || DEFAULT_SONG_TITLE;
  els.tempoButton.textContent = tempoText;
  els.tempoDisplay.textContent = tempoText;
  els.keyDisplay.textContent = keyText;
  els.diatonicKeySelect.value = keySignature.key;
  els.diatonicModeSelect.value = keySignature.mode === "minor" ? "minor" : "major";
  els.meterSelect.value = `${getMeter().numerator}/${getMeter().denominator}`;
  if (els.sustainRange) {
    els.sustainRange.value = String(state.chordSustain);
  }
  if (els.sustainValue) {
    els.sustainValue.textContent = formatChordSustain();
  }
  els.loopButton.classList.toggle("btn-primary", state.loop);
  els.loopButton.classList.toggle("btn-outline-light", !state.loop);
  els.loopButton.setAttribute("aria-pressed", String(state.loop));
  els.metronomeButton.classList.toggle("btn-primary", state.metronome);
  els.metronomeButton.classList.toggle("btn-outline-light", !state.metronome);
  els.metronomeButton.setAttribute("aria-pressed", String(state.metronome));
  els.playButton.classList.toggle("active", state.playing);
  els.playButton.setAttribute("aria-label", state.playing ? "Pause" : "Play");
  els.keyButtonGrid.querySelectorAll("[data-key-value]").forEach((button) => {
    button.classList.toggle("btn-primary", button.dataset.keyValue === keySignature.key);
    button.classList.toggle("btn-outline-light", button.dataset.keyValue !== keySignature.key);
  });
  els.keyModal.querySelectorAll("[data-quality-value]").forEach((button) => {
    button.classList.toggle("btn-primary", button.dataset.qualityValue === keySignature.mode);
    button.classList.toggle("btn-outline-light", button.dataset.qualityValue !== keySignature.mode);
  });
  renderOriginBard();
}

function renderOriginBard() {
  els.originBardIndicator.replaceChildren();
  els.originBardIndicator.classList.toggle("d-none", !state.loadedOriginBardName);
  if (!state.loadedOriginBardName) {
    return;
  }
  els.originBardIndicator.append("original by ");
  const link = document.createElement("a");
  link.href = leaderboardBardUrl(state.loadedOriginBardName);
  link.textContent = state.loadedOriginBardName;
  els.originBardIndicator.append(link);
}

function renderDiatonicChords() {
  const rangeCard = document.createElement("div");
  const rangeControls = document.createElement("div");
  const rangeLabel = document.createElement("div");
  rangeCard.className = "diatonic-range-card";
  rangeControls.className = "diatonic-range-controls";
  rangeLabel.className = "diatonic-range-label";
  rangeLabel.innerHTML = `<span>Preview Range:</span><strong>${escapeHtml(diatonicPreviewRangeLabel())}</strong>`;
  rangeControls.append(
    renderIconButton("\u2193", "Move preview range down", () => shiftDiatonicPreviewRange(-1)),
    renderIconButton("\u2191", "Move preview range up", () => shiftDiatonicPreviewRange(1)),
  );
  rangeCard.append(rangeControls, rangeLabel);

  els.diatonicChordList.replaceChildren(...getScaleChords().map((chord) => {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "diatonic-chord-button";
    button.style.setProperty("--chord-color", chord.color);
    button.innerHTML = `
      <strong>${escapeHtml(chord.symbol)}</strong>
      <span class="diatonic-roman">${escapeHtml(formatRomanNumeral(chord.roman))}</span>
      <span class="diatonic-notes">${escapeHtml(chord.notes.join(" \u00b7 "))}</span>
    `;
    button.addEventListener("click", () => insertOrReplaceChord(chord.symbol));
    return button;
  }), rangeCard);
}

function renderProgression() {
  const events = chordEvents();
  els.progressionChordList.replaceChildren(
    ...events.map((event, index) => renderChordCell(event, index)),
    renderGhostChordCell(),
  );
}

function renderChordCell(event, index) {
  const cell = document.createElement("article");
  const { voicings, voicing, index: voicingIndex } = selectedGuitarVoicingForEvent(event);
  const diatonic = isDiatonicChordSymbol(event.symbol);
  const durationMs = Math.max(1200, event.durationTicks * (60 / getTempo() / PPQ) * 1000);
  cell.className = "guitar-chord-cell";
  cell.dataset.eventId = event.id;
  cell.classList.toggle("is-selected", event.id === state.selectedEventId);
  cell.classList.toggle("is-playing-now", state.playing && event.id === state.selectedEventId);
  cell.style.setProperty("--clip-color", diatonic ? colorForChordSymbol(event.symbol) : nonDiatonicChordColor);
  cell.style.setProperty("--measure-ms", `${durationMs}ms`);
  cell.addEventListener("click", (clickEvent) => {
    if (clickEvent.target.closest("button, select")) {
      return;
    }
    state.selectedEventId = event.id;
    state.paletteAppendMode = false;
    saveAppState();
    renderProgression();
    auditionChord(event);
  });

  const header = document.createElement("div");
  const headerText = document.createElement("div");
  const editButton = renderIconButton("...", `Edit ${event.symbol}`, () => showChordEditModal(event), false, "guitar-chord-edit-button");
  header.className = "guitar-chord-top";
  headerText.className = "guitar-chord-title";
  headerText.innerHTML = `<span>Bar ${index + 1}</span><strong>${escapeHtml(event.symbol)}</strong>`;
  header.append(headerText, editButton);

  const main = document.createElement("button");
  main.type = "button";
  main.className = "guitar-chord-main";
  main.innerHTML = `<strong>${escapeHtml(romanForSymbol(event.symbol) || event.symbol)}</strong>`;
  main.addEventListener("click", () => {
    state.selectedEventId = event.id;
    state.paletteAppendMode = false;
    saveAppState();
    renderProgression();
    auditionChord(event);
  });

  const board = renderMiniGuitarBoard(voicing);
  const controls = document.createElement("div");
  controls.className = "guitar-chord-controls";
  const buttonRow = document.createElement("div");
  buttonRow.className = "guitar-button-row";
  controls.append(
    buttonRow,
    renderPlaystyleSelect(event),
  );
  buttonRow.append(
    renderIconButton("←", "Previous chord", () => transposeEventRoot(event, -1)),
    renderIconButton("→", "Next chord", () => transposeEventRoot(event, 1)),
    renderIconButton("▶", "Play chord", () => auditionChord(event), false, "is-play"),
    renderIconButton("↓", `Previous voicing ${voicings.length ? voicingIndex + 1 : 0} of ${voicings.length}`, () => setChordVoicing(event, -1), voicings.length <= 1),
    renderIconButton("↑", `Next voicing ${voicings.length ? voicingIndex + 1 : 0} of ${voicings.length}`, () => setChordVoicing(event, 1), voicings.length <= 1),
  );
  const progress = document.createElement("span");
  progress.className = "guitar-chord-progress";
  cell.append(header, main, board, controls, progress);
  return cell;
}

function renderGhostChordCell() {
  const cell = document.createElement("article");
  const button = document.createElement("button");
  cell.className = "guitar-chord-cell is-ghost";
  cell.classList.toggle("is-selected", state.paletteAppendMode);
  button.type = "button";
  button.className = "guitar-ghost-button";
  button.innerHTML = `<span aria-hidden="true">+</span>`;
  button.setAttribute("aria-label", "Add next chord from palette");
  button.title = "Add next chord from palette";
  button.addEventListener("click", selectGhostChordCell);
  cell.append(button);
  return cell;
}

function renderIconButton(label, title, onClick, disabled = false, extraClass = "") {
  const button = document.createElement("button");
  button.type = "button";
  button.className = `btn ${extraClass}`.trim();
  button.textContent = label;
  button.title = title;
  button.setAttribute("aria-label", title);
  button.disabled = disabled;
  button.addEventListener("click", onClick);
  return button;
}

function scrollChordIntoViewOnMobile(eventId) {
  if (!window.matchMedia(MOBILE_SCROLL_QUERY).matches) {
    return;
  }
  window.requestAnimationFrame(() => {
    const cell = Array.from(els.progressionChordList.querySelectorAll(".guitar-chord-cell"))
      .find((candidate) => candidate.dataset.eventId === eventId);
    cell?.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
  });
}

function renderPlaystyleSelect(event) {
  const wrapper = document.createElement("div");
  const select = document.createElement("select");
  wrapper.className = "guitar-playstyle-control";
  select.className = "form-select form-select-sm guitar-playstyle-select";
  select.setAttribute("aria-label", `Playstyle for ${event.symbol}`);
  select.replaceChildren(...chordPlayStyleOptions.map((option) => {
    const node = document.createElement("option");
    node.value = option.id;
    node.textContent = option.displayName;
    node.title = option.description;
    return node;
  }));
  select.value = normalizeChordPlayStyle(event.playStyle);
  select.addEventListener("change", () => {
    setEventPlayStyle(event, select.value);
  });
  wrapper.append(
    renderIconButton("←", `Previous playstyle for ${event.symbol}`, () => shiftEventPlayStyle(event, -1), chordPlayStyleOptions.length <= 1, "guitar-playstyle-step"),
    select,
    renderIconButton("→", `Next playstyle for ${event.symbol}`, () => shiftEventPlayStyle(event, 1), chordPlayStyleOptions.length <= 1, "guitar-playstyle-step"),
  );
  return wrapper;
}

function setEventPlayStyle(event, playStyle) {
  event.playStyle = normalizeChordPlayStyle(playStyle);
  touchSong();
  renderProgression();
  auditionChord(event);
}

function shiftEventPlayStyle(event, direction) {
  const current = normalizeChordPlayStyle(event.playStyle);
  const currentIndex = chordPlayStyleOptions.findIndex((option) => option.id === current);
  const nextIndex = positiveModulo(currentIndex + direction, chordPlayStyleOptions.length);
  setEventPlayStyle(event, chordPlayStyleOptions[nextIndex]?.id || DEFAULT_CHORD_PLAY_STYLE);
}

function positiveModulo(value, length) {
  return ((value % length) + length) % length;
}

function renderMiniGuitarBoard(voicing) {
  const board = document.createElement("div");
  board.className = "mini-guitar-board guitar-cell-board";
  if (!voicing) {
    board.textContent = "No fingering";
    return board;
  }

  const firstFret = displayFirstFret(voicing);
  const fretRows = visibleFretRows();
  const barreOverlays = [];
  board.style.setProperty("--mini-fret-rows", String(fretRows));
  board.appendChild(span("mini-guitar-spacer", firstFret > 1 && firstFret !== 8 ? `${firstFret}fr` : ""));
  for (let string = 0; string < 6; string += 1) {
    const stringName = ["E", "A", "D", "G", "B", "E"][string];
    const stringPosition = voicing.positions.find((position) => position.string === string);
    const header = stringPosition ? document.createElement("button") : span("mini-guitar-header", stringName);
    if (stringPosition) {
      const marker = document.createElement("b");
      const noteName = Tone.Frequency(stringPosition.midi, "midi").toNote();
      header.type = "button";
      header.className = "mini-guitar-header";
      header.classList.add("has-note");
      header.dataset.midi = String(stringPosition.midi);
      header.setAttribute("aria-label", `Play ${noteName}`);
      marker.className = stringPosition.isRootNote ? "is-root" : "";
      marker.textContent = stringName;
      header.replaceChildren(marker);
      header.addEventListener("click", (event) => {
        event.stopPropagation();
        playMiniGuitarNote(stringPosition.midi, {
          flashElement: marker,
          triggerElement: header,
        });
      });
    }
    board.appendChild(header);
  }
  for (let fretRow = 0; fretRow < fretRows; fretRow += 1) {
    board.appendChild(span("mini-guitar-fret", String(firstFret + fretRow)));
    const fretNumber = firstFret + fretRow;
    const barreGroups = barreGroupsForFret(voicing, fretNumber);
    for (let string = 0; string < 6; string += 1) {
      const cell = noteCellForFret(string, fretNumber);
      const position = voicing.positions.find((candidate) => candidate.string === string && candidate.fret === fretNumber);
      if (position) {
        cell.classList.add("has-note");
        cell.classList.toggle("has-barre-note", position.isBarre);
        const dot = document.createElement("b");
        dot.className = position.isRootNote ? "is-root" : "";
        dot.textContent = position.symbol || "";
        cell.append(dot);
      }
      board.appendChild(cell);
    }
    barreGroups.forEach((group) => {
      const barre = document.createElement("span");
      barre.className = "mini-guitar-barre";
      barre.style.setProperty("--barre-fret-row", String(fretRow));
      barre.style.setProperty("--barre-start-string", String(group.startString));
      barre.style.setProperty("--barre-string-count", String(group.endString - group.startString + 1));
      barreOverlays.push(barre);
    });
  }
  board.append(...barreOverlays);
  return board;
}

function displayFirstFret(voicing) {
  const firstFret = Math.max(1, Number(voicing.firstFret) || 1);
  const frettedPositions = voicing.positions.filter((position) => position.fret > 0);
  const minFret = Math.min(...frettedPositions.map((position) => position.fret));
  if (voicing.positions.some((position) => position.fret === 0)) {
    return 1;
  }
  return Number.isFinite(minFret) ? minFret : firstFret;
}

function visibleFretRows() {
  return 4;
}

function barreGroupsForFret(voicing, fret) {
  const groups = new Map();
  voicing.positions
    .filter((position) => position.fret === fret && position.isBarre)
    .forEach((position) => {
      const key = `${position.fret}:${position.symbol || "barre"}`;
      const group = groups.get(key) || { startString: position.string, endString: position.string };
      group.startString = Math.min(group.startString, position.string);
      group.endString = Math.max(group.endString, position.string);
      groups.set(key, group);
    });
  return [...groups.values()].filter((group) => group.endString > group.startString);
}

function normalizeBarreValue(value) {
  if (Number.isInteger(value)) {
    return value;
  }
  return value ? true : null;
}

function noteCellForFret(string, fret) {
  const cell = document.createElement("button");
  const midi = GUITAR_TUNING_MIDI[string] + fret;
  const noteName = Tone.Frequency(midi, "midi").toNote();
  cell.type = "button";
  cell.className = "mini-guitar-cell";
  cell.dataset.midi = String(midi);
  cell.setAttribute("aria-label", `Play ${noteName}`);
  cell.addEventListener("click", (event) => {
    event.stopPropagation();
    playMiniGuitarNote(midi, {
      flashElement: cell,
      triggerElement: cell,
    });
  });
  return cell;
}

function playMiniGuitarNote(midi, { flashElement, triggerElement }) {
  const noteName = Tone.Frequency(midi, "midi").toNote();
  flashGuitarCellNoteName(flashElement, noteName.replace(/\d/g, ""));
  playMidi(midi, {
    soundBank: getChordTrack().instrumentName || "acoustic_guitar_nylon",
    duration: 1.35,
    triggerElement,
  }).catch((error) => {
    window.alert(error.message || "Could not play that note.");
  });
}

function flashGuitarCellNoteName(cell, noteName) {
  if (!cell) {
    return;
  }
  cell.querySelector(".mini-guitar-note-flash")?.remove();
  const flash = span("mini-guitar-note-flash", noteName);
  cell.append(flash);
  window.setTimeout(() => flash.remove(), 360);
}

function span(className, text) {
  const node = document.createElement("span");
  node.className = className;
  node.textContent = text;
  return node;
}

function insertOrReplaceChord(symbol) {
  const track = getChordTrack();
  const selected = track.events.find((event) => event.id === state.selectedEventId);
  if (selected && !state.paletteAppendMode) {
    replaceChordSymbol(selected, symbol);
    touchSong();
    render();
    auditionDiatonicPaletteChord(symbol);
    return;
  }

  const events = chordEvents();
  const tick = events.length ? Math.max(...events.map((event) => event.tick + event.durationTicks)) : 0;
  const event = createChordEvent(tick, ticksPerBar(), symbol, DEFAULT_CHORD_PLAY_STYLE);
  track.events.push(event);
  extendFormToFit();
  state.selectedEventId = "";
  state.paletteAppendMode = true;
  touchSong();
  render();
  auditionDiatonicPaletteChord(symbol);
}

function selectGhostChordCell() {
  state.selectedEventId = "";
  state.paletteAppendMode = true;
  saveAppState();
  renderProgression();
}

function replaceChordSymbol(event, symbol) {
  const details = parseChord(symbol);
  Object.assign(event, {
    symbol: details.symbol,
    root: details.root,
    quality: details.quality,
    extensions: details.extensions,
    voicing: details.voicing,
    guitarVoicingIndex: 0,
  });
  if (details.bass) {
    event.bass = details.bass;
  } else {
    delete event.bass;
  }
}

function setChordVoicing(event, delta) {
  const { voicings, index } = selectedGuitarVoicingForEvent(event);
  if (voicings.length <= 1) {
    return;
  }
  event.guitarVoicingIndex = (index + delta + voicings.length) % voicings.length;
  state.selectedEventId = event.id;
  state.paletteAppendMode = false;
  touchSong();
  renderProgression();
  auditionChord(event);
}

function transposeEventRoot(event, delta) {
  const token = Chord.tokenize(event.symbol);
  const root = token?.[0] || parseChord(event.symbol).root || "C";
  const suffix = String(event.symbol).slice(String(root).length);
  const nextRoot = chromaticRoots[(Note.chroma(root) + delta + 120) % 12];
  replaceChordSymbol(event, `${nextRoot}${suffix}`);
  state.selectedEventId = event.id;
  state.paletteAppendMode = false;
  touchSong();
  render();
  auditionChord(event);
}

async function auditionChord(event) {
  const previewGeneration = abortChordPreview();
  try {
    const durationMs = event?.durationTicks ? Math.max(1800, event.durationTicks * (60 / getTempo() / PPQ) * 1000) : 1800;
    await playStyledChordMidis(eventMidiNotes(event), {
      soundBank: getChordTrack().instrumentName || "acoustic_guitar_nylon",
      playStyle: event.playStyle,
      durationMs,
      sustain: state.chordSustain,
      shouldContinue: () => previewGeneration === state.previewGeneration,
    });
  } catch (error) {
    window.alert(error.message || "Could not play that chord.");
  }
}

async function auditionDiatonicPaletteChord(symbol) {
  const previewGeneration = abortChordPreview();
  try {
    await playStyledChordMidis(diatonicPreviewMidis(symbol), {
      soundBank: getChordTrack().instrumentName || "acoustic_guitar_nylon",
      playStyle: DEFAULT_CHORD_PLAY_STYLE,
      durationMs: 1800,
      sustain: state.chordSustain,
      shouldContinue: () => previewGeneration === state.previewGeneration,
    });
  } catch (error) {
    window.alert(error.message || "Could not play that chord.");
  }
}

function abortChordPreview() {
  state.previewGeneration += 1;
  releaseActiveSampler(Tone.now(), { dispose: true });
  return state.previewGeneration;
}

async function playStyledChordMidis(midis, { soundBank = "acoustic_guitar_nylon", playStyle = DEFAULT_CHORD_PLAY_STYLE, durationMs = 1200, startTime = null, sustain = state.chordSustain, shouldContinue = null } = {}) {
  const notes = [...midis].sort((a, b) => a - b);
  if (!notes.length) {
    return;
  }

  const samplerState = await ensureSampler(soundBank);
  if (shouldContinue && !shouldContinue()) {
    return;
  }
  const sampler = samplerState.sampler;
  const config = soundfontConfig(soundBank);
  const value = normalizeChordPlayStyle(playStyle);
  const playStyleConfig = getChordPlayStyle(value);
  const beatMs = 60000 / getTempo();
  const now = Math.max(Tone.now(), Number(startTime) || 0);
  const strumGapSlowMs = Math.min(45, Math.max(12, beatMs / 18));
  const strumGapFastMs = Math.min(22, Math.max(6, beatMs / 36));
  const sustainMultiplier = normalizeChordSustain(sustain);

  const trigger = (midi, noteOffsetMs, velocityScale = 1, noteDurationMs = durationMs) => {
    sampler?.triggerAttackRelease(
      Tone.Frequency(midi, "midi").toNote(),
      Math.max(0.05, (noteDurationMs * sustainMultiplier) / 1000),
      now + (Math.max(0, noteOffsetMs) / 1000),
      config.defaultVelocity * velocityScale,
    );
  };
  const stroke = (direction, strokeOffsetMs, velocityScale = 1, strokeGapMs = strumGapSlowMs) => {
    const strokeNotes = direction === "up" ? [...notes].reverse() : notes;
    strokeNotes.forEach((midi, index) => trigger(midi, strokeOffsetMs + (index * strokeGapMs), velocityScale, durationMs - strokeOffsetMs));
  };

  if (playStyleConfig?.type === "block") {
    notes.forEach((midi) => trigger(midi, 0));
  } else if (playStyleConfig?.type === "arpeggio") {
    const stepMs = beatMs * (4 / (Number(playStyleConfig.subdivision) || 8));
    (playStyleConfig.notes || []).forEach((noteIndex, step) => {
      const resolvedIndex = noteIndexForPlayStyle(noteIndex, notes.length);
      if (resolvedIndex === null) {
        return;
      }
      trigger(notes[resolvedIndex], step * stepMs, 1, Math.max(70, stepMs * 0.86));
    });
  } else if (playStyleConfig?.type === "strum") {
    (playStyleConfig.strokes || []).forEach((strokeConfig) => {
      const offsetMs = stepToBeatOffset(strokeConfig.step, playStyleConfig.subdivision) * beatMs;
      if (offsetMs < durationMs) {
        stroke(strokeConfig.direction, offsetMs, strokeConfig.velocity, strumGapFastMs);
      }
    });
  } else {
    stroke("down", 0);
  }
}

function setTempo(bpm) {
  const nextBpm = Math.max(40, Math.min(240, Math.round(Number(bpm) || getTempo())));
  state.song.timeline.defaultTempoBpm = nextBpm;
  state.song.timeline.tempoEvents = [{ tick: 0, bpm: nextBpm }];
  touchSong();
  renderControls();
}

function tapTempo() {
  const now = performance.now();
  const previousTap = state.tapTempoTimes.at(-1) || 0;
  if (now - previousTap > 2500) {
    state.tapTempoTimes = [];
  }
  state.tapTempoTimes.push(now);
  state.tapTempoTimes = state.tapTempoTimes.slice(-5);
  if (state.tapTempoTimes.length < 2) {
    return;
  }
  const intervals = state.tapTempoTimes.slice(1).map((tapTime, index) => tapTime - state.tapTempoTimes[index]);
  const average = intervals.reduce((total, interval) => total + interval, 0) / intervals.length;
  setTempo(60000 / average);
}

function setKeySignatureValue(updates) {
  const current = getKeySignature();
  const key = keys.includes(updates.key) ? updates.key : current.key;
  const mode = updates.mode === "minor" ? "minor" : updates.mode === "major" ? "major" : current.mode;
  state.song.timeline.keySignatureEvents = [{ tick: 0, key, mode }];
  touchSong();
  render();
}

function updateMeter() {
  const [numerator, denominator] = els.meterSelect.value.split("/").map(Number);
  state.song.timeline.timeSignatureEvents = [{ tick: 0, numerator, denominator }];
  normalizeChordDurations();
  touchSong();
  render();
}

function normalizeChordDurations() {
  chordEvents().forEach((event, index) => {
    event.tick = index * ticksPerBar();
    event.durationTicks = ticksPerBar();
  });
  extendFormToFit();
}

function removeSelected() {
  const track = getChordTrack();
  const index = track.events.findIndex((event) => event.id === state.selectedEventId);
  if (index < 0) {
    return;
  }
  track.events.splice(index, 1);
  normalizeChordDurations();
  state.selectedEventId = chordEvents()[Math.max(0, index - 1)]?.id || "";
  state.paletteAppendMode = !state.selectedEventId;
  touchSong();
  render();
}

function transposeSong(semitones) {
  chordEvents().forEach((event) => {
    const token = Chord.tokenize(event.symbol);
    const root = token?.[0] || parseChord(event.symbol).root || "C";
    const suffix = String(event.symbol).slice(String(root).length);
    const nextRoot = chromaticRoots[(Note.chroma(root) + semitones + 1200) % 12];
    replaceChordSymbol(event, `${nextRoot}${suffix}`);
  });
  const currentKey = getKeySignature();
  const keyChroma = Note.chroma(currentKey.key);
  if (Number.isFinite(keyChroma)) {
    state.song.timeline.keySignatureEvents = [{
      tick: 0,
      key: keys.find((key) => Note.chroma(key) === (keyChroma + semitones + 1200) % 12) || currentKey.key,
      mode: currentKey.mode,
    }];
  }
  touchSong();
  render();
}

function extendFormToFit() {
  const durationTicks = Math.max(ticksPerBar(), ...chordEvents().map((event) => event.tick + event.durationTicks));
  const section = state.song.form.sections[0] || { id: makeId("sec"), name: "Verse 1", startTick: 0, durationTicks };
  section.startTick = 0;
  section.durationTicks = durationTicks;
  state.song.form.sections = [section];
}

function togglePlayback() {
  if (state.playing) {
    stopPlayback(false, { releaseSounds: true });
    return;
  }
  startPlayback();
}

async function startPlayback() {
  stopPlayback(false, { releaseSounds: true });
  state.playing = true;
  state.playbackGeneration += 1;
  renderControls();
  try {
    await warmUpPlaybackAudio();
    schedulePlaybackCycle(Tone.now() + 0.08, state.playbackGeneration);
  } catch (error) {
    stopPlayback(false, { releaseSounds: true });
    window.alert(error.message || "Could not start playback.");
  }
}

async function warmUpPlaybackAudio() {
  await Tone.start();
  const warmups = [ensureSampler(getChordTrack().instrumentName || "acoustic_guitar_nylon")];
  if (state.metronome) {
    warmups.push(ensureDrumSampler());
  }
  await Promise.all(warmups);
  if (!state.metronomeSynth) {
    state.metronomeSynth = new Tone.MembraneSynth({ volume: -10 }).toDestination();
  }
}

function schedulePlaybackCycle(cycleStartTime = Tone.now() + 0.08, generation = state.playbackGeneration) {
  clearPlayTimers();
  const tempo = getTempo();
  const msPerTick = (60 / tempo / PPQ) * 1000;
  const secondsPerTick = msPerTick / 1000;
  const events = chordEvents();
  const totalDuration = Math.max(ticksPerBar(), ...events.map((event) => event.tick + event.durationTicks));
  const totalDurationMs = totalDuration * msPerTick;
  const totalDurationSeconds = totalDuration * secondsPerTick;
  const delayUntil = (audioTime) => Math.max(0, (audioTime - Tone.now()) * 1000);
  events.forEach((event) => {
    const eventStartTime = cycleStartTime + (event.tick * secondsPerTick);
    playStyledChordMidis(eventMidiNotes(event), {
      soundBank: getChordTrack().instrumentName || "acoustic_guitar_nylon",
      playStyle: event.playStyle,
      durationMs: Math.max(1800, event.durationTicks * msPerTick),
      startTime: eventStartTime,
      sustain: state.chordSustain,
    }).catch(() => {});
    state.playTimers.push(window.setTimeout(() => {
      if (!state.playing || generation !== state.playbackGeneration) {
        return;
      }
      state.selectedEventId = event.id;
      renderProgression();
      scrollChordIntoViewOnMobile(event.id);
    }, delayUntil(eventStartTime)));
  });
  if (state.metronome) {
    const beatCount = Math.ceil(totalDuration / ticksPerBeat());
    for (let beat = 0; beat < beatCount; beat += 1) {
      const beatStartTime = cycleStartTime + (beat * ticksPerBeat() * secondsPerTick);
      state.playTimers.push(window.setTimeout(() => playMetronomeClick(beat % getMeter().numerator === 0), delayUntil(beatStartTime)));
    }
  }
  state.playTimers.push(window.setTimeout(() => {
    if (!state.playing || generation !== state.playbackGeneration) {
      return;
    }
    if (state.loop) {
      schedulePlaybackCycle(cycleStartTime + totalDurationSeconds, generation);
      return;
    }
    stopPlayback(false);
  }, state.loop ? Math.max(0, totalDurationMs - 80) : totalDurationMs + 80));
}

function playMetronomeClick(accent) {
  playDrumMidi(accent ? 76 : 77, accent ? 0.92 : 0.68).catch(() => {
    state.metronomeSynth = state.metronomeSynth || new Tone.MembraneSynth({ volume: -10 }).toDestination();
    state.metronomeSynth.triggerAttackRelease(accent ? "C5" : "C4", "16n", Tone.now(), accent ? 0.62 : 0.38);
  });
}

async function playDrumMidi(midi, velocity = 0.8) {
  await ensureDrumSampler();
  state.drumSampler?.triggerAttackRelease(Tone.Frequency(midi, "midi").toNote(), 0.16, Tone.now(), velocity);
}

async function ensureDrumSampler() {
  await Tone.start();
  if (state.drumSamplerReady && state.drumSampler) {
    return;
  }
  if (state.drumSamplerLoadPromise) {
    await state.drumSamplerLoadPromise;
    return;
  }
  const soundfont = window.MIDI?.Soundfont?.drums;
  if (!soundfont) {
    throw new Error("Drums soundfont is not loaded.");
  }
  const output = new Tone.Gain(0.9).toDestination();
  state.drumSamplerReady = false;
  state.drumSampler = new Tone.Sampler({
    urls: { ...soundfont },
    attack: 0,
    release: 0.2,
    onload() {
      state.drumSamplerReady = true;
    },
  }).connect(output);
  state.drumSamplerLoadPromise = Tone.loaded().then(() => {
    state.drumSamplerReady = true;
  });
  await state.drumSamplerLoadPromise;
}

function stopPlayback(resetSelection = true, { releaseSounds = false } = {}) {
  state.playing = false;
  state.playbackGeneration += 1;
  clearPlayTimers();
  if (releaseSounds) {
    releaseAllPlaybackSounds();
  }
  if (resetSelection) {
    state.selectedEventId = "";
    state.paletteAppendMode = true;
  }
  render();
}

function releaseAllPlaybackSounds() {
  const now = Tone.now();
  releaseActiveSampler(now, { dispose: true });
  state.drumSampler?.releaseAll?.(now);
  state.metronomeSynth?.triggerRelease?.(now);
}

function clearPlayTimers() {
  state.playTimers.forEach((timerId) => window.clearTimeout(timerId));
  state.playTimers = [];
}

function normalizeSong(input) {
  const fallback = createDefaultSong();
  const song = {
    ...fallback,
    ...(input || {}),
    schema: "ChordSongJSON",
    schemaVersion: input?.schemaVersion || "0.1.0",
    ppq: Number(input?.ppq) || PPQ,
    timeline: { ...fallback.timeline, ...(input?.timeline || {}) },
    form: { ...fallback.form, ...(input?.form || {}) },
    metadata: { ...fallback.metadata, ...(input?.metadata || {}) },
    tracks: Array.isArray(input?.tracks) && input.tracks.length ? input.tracks : fallback.tracks,
    markers: Array.isArray(input?.markers) ? input.markers : fallback.markers,
  };
  song.timeline.tempoEvents = Array.isArray(song.timeline.tempoEvents) && song.timeline.tempoEvents.length ? song.timeline.tempoEvents : fallback.timeline.tempoEvents;
  song.timeline.timeSignatureEvents = Array.isArray(song.timeline.timeSignatureEvents) && song.timeline.timeSignatureEvents.length ? song.timeline.timeSignatureEvents : fallback.timeline.timeSignatureEvents;
  song.timeline.keySignatureEvents = Array.isArray(song.timeline.keySignatureEvents) && song.timeline.keySignatureEvents.length ? song.timeline.keySignatureEvents : fallback.timeline.keySignatureEvents;
  song.form.sections = Array.isArray(song.form.sections) && song.form.sections.length ? song.form.sections : fallback.form.sections;
  song.form.playOrder = Array.isArray(song.form.playOrder) ? song.form.playOrder : [];
  song.tracks = song.tracks.map(normalizeTrack);
  if (!song.tracks.some((track) => track.type === "chords")) {
    song.tracks.unshift(makeTrack("track_chords", "Chords", "chords", "acoustic_guitar_nylon"));
  }
  return song;
}

function normalizeChordSustain(value) {
  const sustain = Number(value);
  return Number.isFinite(sustain) ? Math.max(0.25, Math.min(2, sustain)) : DEFAULT_CHORD_SUSTAIN;
}

function formatChordSustain(value = state.chordSustain) {
  return `${Math.round(normalizeChordSustain(value) * 100)}%`;
}

function setChordSustain(value) {
  state.chordSustain = normalizeChordSustain(value);
  if (els.sustainRange) {
    els.sustainRange.value = String(state.chordSustain);
  }
  if (els.sustainValue) {
    els.sustainValue.textContent = formatChordSustain();
  }
  saveAppState();
}

function normalizeTrack(track) {
  return {
    ...track,
    instrumentName: track.type === "chords" ? (track.instrumentName || "acoustic_guitar_nylon") : track.instrumentName,
    muted: typeof track.muted === "boolean" ? track.muted : false,
    events: Array.isArray(track.events) ? track.events.map(normalizeEvent) : [],
  };
}

function normalizeEvent(event) {
  if (event?.eventType !== "chord") {
    return event;
  }
  const parsed = parseChord(event.symbol);
  return {
    ...event,
    symbol: event.symbol || parsed.symbol,
    root: event.root || parsed.root,
    quality: event.quality || parsed.quality,
    extensions: Array.isArray(event.extensions) ? event.extensions : parsed.extensions,
    voicing: Array.isArray(event.voicing) && event.voicing.length ? event.voicing : parsed.voicing,
    playStyle: normalizeChordPlayStyle(event.playStyle),
    guitarVoicingIndex: Math.max(0, Number(event.guitarVoicingIndex) || 0),
  };
}

function createNewSong() {
  stopPlayback(false, { releaseSounds: true });
  state.song = createDefaultSong();
  state.selectedEventId = "";
  state.paletteAppendMode = true;
  state.serverSongId = null;
  state.serverSongSignature = null;
  state.serverSongLocked = false;
  state.loadedOriginBardName = "";
  touchSong();
  render();
}

function touchSong() {
  state.song.updatedAt = new Date().toISOString();
  saveAppState();
  renderFileMenuState();
}

function saveAppState() {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({
      version: 1,
      savedAt: new Date().toISOString(),
      song: state.song,
      settings: {
        selectedEventId: state.selectedEventId,
        paletteAppendMode: state.paletteAppendMode,
        defaultChordPlayStyle: state.defaultChordPlayStyle,
        chordSustain: state.chordSustain,
        loop: state.loop,
        metronome: state.metronome,
        serverSongId: state.serverSongId,
        serverSongSignature: state.serverSongSignature,
        serverSongLocked: state.serverSongLocked,
      },
    }));
    window.localStorage.setItem(CHORD_PROGRESSIONS_STORAGE_KEY, JSON.stringify({
      version: 1,
      savedAt: new Date().toISOString(),
      song: state.song,
      settings: {
        defaultChordPlayStyle: state.defaultChordPlayStyle,
        chordSustain: state.chordSustain,
        chordRenderMode: "guitar",
        serverSongId: state.serverSongId,
        serverSongSignature: state.serverSongSignature,
        serverSongLocked: state.serverSongLocked,
      },
    }));
  } catch {
    // Local storage can be disabled; editing should continue.
  }
}

function loadStoredState() {
  try {
    const stored = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || "{}");
    if (stored.song) {
      state.song = normalizeSong(stored.song);
      state.selectedEventId = stored.settings?.selectedEventId || "";
      state.paletteAppendMode = stored.settings?.paletteAppendMode ?? !state.selectedEventId;
      state.defaultChordPlayStyle = normalizeChordPlayStyle(stored.settings?.defaultChordPlayStyle);
      state.chordSustain = normalizeChordSustain(stored.settings?.chordSustain);
      state.loop = stored.settings?.loop !== false;
      state.metronome = Boolean(stored.settings?.metronome);
      state.serverSongId = stored.settings?.serverSongId || null;
      state.serverSongSignature = stored.settings?.serverSongSignature || null;
      state.serverSongLocked = Boolean(stored.settings?.serverSongLocked);
    }
  } catch {
    state.song = createDefaultSong();
  }
}

async function apiRequest(path, options = {}) {
  const response = await fetch(path, {
    credentials: "same-origin",
    headers: { "Content-Type": "application/json", ...(options.headers || {}) },
    ...options,
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(body.error || "The server could not complete that request.");
    error.status = response.status;
    throw error;
  }
  return body;
}

async function saveSongToServer() {
  if (!state.serverSongId || state.serverSongLocked) {
    await showSaveSongModal();
    return;
  }
  await saveSongToServerWithOptions({ title: state.song.title, shared: state.song.shared, serverSongId: state.serverSongId });
}

async function showSaveSongModal() {
  const modal = window.bootstrap.Modal.getOrCreateInstance(els.saveSongModal);
  state.saveModalSelectedSongId = state.serverSongLocked ? null : state.serverSongId;
  els.saveSongNameInput.value = state.serverSongLocked ? `${state.song.title || DEFAULT_SONG_TITLE} Copy`.slice(0, 160) : state.song.title || DEFAULT_SONG_TITLE;
  els.saveSongSharedCheckbox.checked = Boolean(state.song.shared);
  state.loadingSaveSongs = true;
  renderSaveSongList([], "Loading saved progressions...");
  renderSaveSongDuplicateState();
  renderSaveSongModalState();
  modal.show();
  try {
    const body = await apiRequest("/api/songs?mode=my");
    state.saveModalSongs = body.songs || [];
    renderSaveSongList(state.saveModalSongs);
    renderSaveSongDuplicateState();
  } catch (error) {
    state.saveModalSongs = [];
    renderSaveSongList([], error.message || "Could not load saved progressions.");
  } finally {
    state.loadingSaveSongs = false;
    renderSaveSongModalState();
  }
}

async function saveSongFromModal() {
  const duplicateSong = currentSaveDuplicateSong();
  let serverSongId = state.saveModalSelectedSongId;
  if (duplicateSong && !window.confirm(`Are you sure you want to save over "${duplicateSong.title || DEFAULT_SONG_TITLE}"?`)) {
    return;
  }
  if (duplicateSong) {
    serverSongId = duplicateSong.id;
  }
  await saveSongToServerWithOptions({ title: els.saveSongNameInput.value, shared: els.saveSongSharedCheckbox.checked, serverSongId });
}

async function saveSongToServerWithOptions(options = {}) {
  if (state.savingSong) {
    return;
  }
  state.savingSong = true;
  renderFileMenuState();
  renderSaveSongModalState();
  try {
    const selectedSong = options.serverSongId ? state.saveModalSongs.find((song) => song.id === options.serverSongId) : null;
    if (selectedSong?.songId) {
      state.song.id = selectedSong.songId;
    } else if (!options.serverSongId && state.serverSongId) {
      state.song.id = makeId("song");
    }
    state.song.title = String(options.title ?? state.song.title).trim().slice(0, 160) || DEFAULT_SONG_TITLE;
    state.song.shared = Boolean(options.shared ?? state.song.shared);
    const body = await apiRequest("/api/songs", {
      method: "POST",
      body: JSON.stringify({ songId: state.song.id, title: state.song.title, shared: state.song.shared, song: state.song }),
    });
    state.serverSongId = body.song?.id ? String(body.song.id) : state.serverSongId;
    state.serverSongLocked = Boolean(body.song?.locked);
    state.loadedOriginBardName = "";
    if (body.song?.songId) {
      state.song.id = body.song.songId;
    }
    state.serverSongSignature = songServerSignature();
    saveAppState();
    render();
    window.bootstrap.Modal.getOrCreateInstance(els.saveSongModal).hide();
  } catch (error) {
    window.alert(error.message || "Save failed.");
  } finally {
    state.savingSong = false;
    renderFileMenuState();
    renderSaveSongModalState();
  }
}

function renderSaveSongList(songs, message = "") {
  els.saveSongMessage.textContent = message || (songs.length ? "" : "No saved progressions yet.");
  els.saveExistingSongList.replaceChildren(...songs.map((song) => {
    const button = document.createElement("button");
    button.className = `list-group-item list-group-item-action bg-dark text-light border-secondary${song.id === state.saveModalSelectedSongId ? " active" : ""}`;
    button.type = "button";
    button.innerHTML = `<div class="d-flex w-100 justify-content-between gap-3"><strong>${escapeHtml(song.title || DEFAULT_SONG_TITLE)}</strong><small class="text-secondary">${escapeHtml(formatSongDate(song.updatedAt))}${song.shared ? " - Shared" : ""}</small></div>`;
    button.addEventListener("click", () => {
      state.saveModalSelectedSongId = song.id;
      els.saveSongNameInput.value = song.title || DEFAULT_SONG_TITLE;
      els.saveSongSharedCheckbox.checked = Boolean(song.shared);
      renderSaveSongList(state.saveModalSongs);
      renderSaveSongDuplicateState();
    });
    return button;
  }));
}

function renderSaveSongDuplicateState() {
  const duplicateSong = currentSaveDuplicateSong();
  els.saveSongNameInput.classList.toggle("is-duplicate", Boolean(duplicateSong));
  els.saveSongDuplicateMessage.textContent = duplicateSong ? "A progression with this name already exists." : "";
}

function currentSaveDuplicateSong() {
  const title = normalizeComparableTitle(els.saveSongNameInput.value);
  return title ? state.saveModalSongs.find((song) => normalizeComparableTitle(song.title) === title) || null : null;
}

function renderSaveSongModalState() {
  els.confirmSaveSongButton.disabled = state.savingSong || state.loadingSaveSongs;
  els.confirmSaveSongButton.textContent = state.savingSong ? "Saving..." : "OK";
}

async function showLoadSongModal() {
  const modal = window.bootstrap.Modal.getOrCreateInstance(els.loadSongModal);
  renderLoadSongList([], "Loading saved songs...");
  modal.show();
  try {
    const [ownedBody, sharedBody] = await Promise.all([apiRequest("/api/songs?mode=my"), apiRequest("/api/songs?mode=shared")]);
    state.loadModalSongs = [...(ownedBody.songs || []), ...(sharedBody.songs || []).filter((song) => !song.owned)];
    renderLoadSongList(state.loadModalSongs);
  } catch (error) {
    state.loadModalSongs = [];
    renderLoadSongList([], error.message || "Could not load songs.");
  }
}

function renderLoadSongList(songs, message = "") {
  renderLoadSongModeTabs();
  const sharedMode = state.loadModalMode === "shared";
  if (els.loadSongOwnerSortButton) {
    els.loadSongOwnerSortButton.textContent = sharedMode ? "Bard" : "Access";
    els.loadSongOwnerSortButton.dataset.loadSongSort = sharedMode ? "bardName" : "access";
  }
  const sortedSongs = sortSongsForList(songs.filter((song) => (sharedMode ? !song.owned : song.owned)));
  els.loadSongMessage.textContent = message || (sortedSongs.length ? "" : `No ${sharedMode ? "shared" : "owned"} progressions yet.`);
  els.loadSongList.replaceChildren(...sortedSongs.map((song) => {
    const row = document.createElement("div");
    row.className = "list-group-item list-group-item-action bg-dark text-light border-secondary song-list-row";
    row.setAttribute("role", "button");
    row.tabIndex = 0;
    const ownerCell = sharedMode
      ? `<a class="song-list-cell text-secondary" href="${escapeHtml(leaderboardBardUrl(song.bardName))}">${escapeHtml(song.bardName || "Shared")}</a>`
      : `<span class="song-list-cell text-secondary">${escapeHtml(formatSongAccess(song))}</span>`;
    row.innerHTML = `<strong class="song-list-cell song-list-title-cell">${escapeHtml(song.title || DEFAULT_SONG_TITLE)}</strong><span class="song-list-cell text-secondary">${escapeHtml(formatSongDate(song.updatedAt))}</span>${ownerCell}`;
    row.querySelector("a")?.addEventListener("click", (event) => event.stopPropagation());
    row.addEventListener("click", () => loadSongFromServer(song.id));
    row.addEventListener("keydown", (event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        loadSongFromServer(song.id);
      }
    });
    return row;
  }));
  document.querySelectorAll("[data-load-song-sort]").forEach((button) => {
    button.classList.toggle("is-active", button.dataset.loadSongSort === state.loadModalSortKey);
  });
}

function renderLoadSongModeTabs() {
  const shared = state.loadModalMode === "shared";
  els.loadSongOwnedTab.classList.toggle("active", !shared);
  els.loadSongOwnedTab.setAttribute("aria-selected", shared ? "false" : "true");
  els.loadSongSharedTab.classList.toggle("active", shared);
  els.loadSongSharedTab.setAttribute("aria-selected", shared ? "true" : "false");
}

function sortSongsForList(songs) {
  const direction = state.loadModalSortDirection === "asc" ? 1 : -1;
  const valueForSong = (song) => {
    if (state.loadModalSortKey === "bardName") return song.owned ? "You" : song.bardName || "Shared";
    if (state.loadModalSortKey === "updatedAt") return new Date(song.updatedAt).getTime() || 0;
    if (state.loadModalSortKey === "access") return formatSongAccess(song);
    return song.title || DEFAULT_SONG_TITLE;
  };
  return [...songs].sort((first, second) => {
    const firstValue = valueForSong(first);
    const secondValue = valueForSong(second);
    if (typeof firstValue === "number" || typeof secondValue === "number") {
      return ((firstValue || 0) - (secondValue || 0)) * direction;
    }
    return String(firstValue).localeCompare(String(secondValue), undefined, { sensitivity: "base" }) * direction;
  });
}

async function loadSongFromServer(songId) {
  renderLoadSongList([], "Loading song...");
  try {
    const body = await apiRequest(`/api/songs/${encodeURIComponent(songId)}`);
    const loadedSong = body.song?.song;
    if (!loadedSong) {
      throw new Error("The server did not return song JSON.");
    }
    stopPlayback(false, { releaseSounds: true });
    state.song = normalizeSong(loadedSong);
    state.serverSongId = body.song.id ? String(body.song.id) : null;
    state.serverSongLocked = Boolean(body.song.locked || !body.song.owned);
    state.loadedOriginBardName = body.song.owned ? "" : String(body.song.bardName || "").trim();
    state.serverSongSignature = state.serverSongId ? songServerSignature() : null;
    state.selectedEventId = chordEvents()[0]?.id || "";
    state.paletteAppendMode = !state.selectedEventId;
    saveAppState();
    render();
    window.bootstrap.Modal.getOrCreateInstance(els.loadSongModal).hide();
  } catch (error) {
    renderLoadSongList([], error.message || "Could not load that song.");
  }
}

function serverComparableSong(song = state.song) {
  return { ...song, title: String(song.title || DEFAULT_SONG_TITLE).trim() || DEFAULT_SONG_TITLE, updatedAt: "" };
}

function songServerSignature(song = state.song) {
  return JSON.stringify(serverComparableSong(song));
}

function hasUnsavedServerChanges() {
  return Boolean(state.serverSongId && state.serverSongSignature && songServerSignature() !== state.serverSongSignature);
}

function renderFileMenuState() {
  els.unsavedChangesIndicator.classList.toggle("d-none", !hasUnsavedServerChanges());
  els.fileSaveButton.disabled = state.savingSong;
  els.fileSaveAsButton.disabled = state.savingSong;
}

function formatSongAccess(song) {
  return [song.shared ? "Public" : ""].filter(Boolean).join(", ") || "Private";
}

function formatSongDate(value) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function leaderboardBardUrl(bardName) {
  return `/leaderboard/?${new URLSearchParams({ bardName: String(bardName || "") })}`;
}

function normalizeComparableTitle(value) {
  return String(value || "").trim().replace(/\s+/g, " ").toLocaleLowerCase();
}

function chordTypeOptions() {
  return guitarFingeringData()?.chords?.length ? guitarFingeringData().chords : ["Major", "Minor", "7", "m7", "sus2", "sus4"];
}

function chordTypeToSuffix(type) {
  if (type === "Major") return "";
  if (type === "Minor") return "m";
  return type;
}

function renderChordEditOptions() {
  els.chordEditRootSelect.replaceChildren(...GUITAR_PITCHES.map((root) => {
    const node = document.createElement("option");
    node.value = root;
    node.textContent = root;
    return node;
  }));
  els.chordEditTypeSelect.replaceChildren(...chordTypeOptions().map((type) => {
    const node = document.createElement("option");
    node.value = type;
    node.textContent = type;
    return node;
  }));
}

function showChordEditModal(event) {
  state.editingChordId = event.id;
  const root = normalizeGuitarRoot(event.root || Chord.tokenize(event.symbol)?.[0]) || "C";
  const type = chordTypeOptions().includes(guitarChordToken(event.symbol)) ? guitarChordToken(event.symbol) : "Major";
  els.chordEditRootSelect.value = root;
  els.chordEditTypeSelect.value = type;
  window.bootstrap.Modal.getOrCreateInstance(els.chordEditModal).show();
}

function updateEditingChord() {
  const event = chordEvents().find((candidate) => candidate.id === state.editingChordId);
  if (!event) {
    return;
  }
  replaceChordSymbol(event, `${els.chordEditRootSelect.value}${chordTypeToSuffix(els.chordEditTypeSelect.value)}`);
  state.selectedEventId = event.id;
  state.paletteAppendMode = false;
  touchSong();
  render();
  auditionChord(event);
}

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function wireStaticOptions() {
  renderChordEditOptions();
  els.diatonicKeySelect.replaceChildren(...keys.map((key) => {
    const node = document.createElement("option");
    node.value = key;
    node.textContent = key;
    return node;
  }));
  els.keyButtonGrid.replaceChildren(...keys.map((key) => {
    const button = document.createElement("button");
    button.className = "btn btn-outline-light";
    button.type = "button";
    button.dataset.keyValue = key;
    button.textContent = key;
    button.addEventListener("click", () => setKeySignatureValue({ key }));
    return button;
  }));
}

function wireEvents() {
  els.confirmSaveSongButton.addEventListener("click", saveSongFromModal);
  els.chordEditRootSelect.addEventListener("change", updateEditingChord);
  els.chordEditTypeSelect.addEventListener("change", updateEditingChord);
  els.diatonicKeySelect.addEventListener("change", () => setKeySignatureValue({ key: els.diatonicKeySelect.value }));
  els.diatonicModeSelect.addEventListener("change", () => setKeySignatureValue({ mode: els.diatonicModeSelect.value }));
  els.fileLoadButton.addEventListener("click", showLoadSongModal);
  els.fileNewButton.addEventListener("click", createNewSong);
  els.fileSaveAsButton.addEventListener("click", showSaveSongModal);
  els.fileSaveButton.addEventListener("click", saveSongToServer);
  els.loopButton.addEventListener("click", () => {
    state.loop = !state.loop;
    saveAppState();
    renderControls();
  });
  els.metronomeButton.addEventListener("click", () => {
    state.metronome = !state.metronome;
    saveAppState();
    renderControls();
    if (state.playing) {
      startPlayback();
    }
  });
  els.meterSelect.addEventListener("change", updateMeter);
  els.playButton.addEventListener("click", togglePlayback);
  els.saveSongNameInput.addEventListener("input", () => {
    state.saveModalSelectedSongId = null;
    renderSaveSongDuplicateState();
    renderSaveSongList(state.saveModalSongs);
  });
  els.songTitleInput.addEventListener("blur", () => {
    state.song.title = els.songTitleInput.textContent.trim().slice(0, 160) || DEFAULT_SONG_TITLE;
    touchSong();
    renderControls();
  });
  els.stopButton.addEventListener("click", () => stopPlayback(true, { releaseSounds: true }));
  els.sustainRange?.addEventListener("input", () => setChordSustain(els.sustainRange.value));
  els.tapTempoButton.addEventListener("click", tapTempo);
  els.tempoButton.addEventListener("click", () => {
    state.tapTempoTimes = [];
    window.bootstrap.Modal.getOrCreateInstance(els.tempoModal).show();
  });
  els.tempoModal.querySelectorAll("[data-tempo-step]").forEach((button) => {
    button.addEventListener("click", () => {
      state.tapTempoTimes = [];
      setTempo(getTempo() + Number(button.dataset.tempoStep));
    });
  });
  els.tempoModal.querySelectorAll("[data-tempo-preset]").forEach((button) => {
    button.addEventListener("click", () => {
      state.tapTempoTimes = [];
      setTempo(button.dataset.tempoPreset);
    });
  });
  els.transportSettingsButton.addEventListener("click", () => window.bootstrap.Modal.getOrCreateInstance(els.transportModal).show());
  els.transposeButton.addEventListener("click", () => window.bootstrap.Modal.getOrCreateInstance(els.transposeModal).show());
  els.transposeModal.querySelectorAll("[data-transpose]").forEach((button) => {
    button.addEventListener("click", () => transposeSong(Number(button.dataset.transpose) || 0));
  });
  els.keyModal.querySelectorAll("[data-quality-value]").forEach((button) => {
    button.addEventListener("click", () => setKeySignatureValue({ mode: button.dataset.qualityValue }));
  });
  document.querySelectorAll("[data-load-mode]").forEach((button) => {
    button.addEventListener("click", () => {
      state.loadModalMode = button.dataset.loadMode === "shared" ? "shared" : "owned";
      renderLoadSongList(state.loadModalSongs);
    });
  });
  document.querySelectorAll("[data-load-song-sort]").forEach((button) => {
    button.addEventListener("click", () => {
      const key = button.dataset.loadSongSort || "updatedAt";
      if (state.loadModalSortKey === key) {
        state.loadModalSortDirection = state.loadModalSortDirection === "asc" ? "desc" : "asc";
      } else {
        state.loadModalSortKey = key;
        state.loadModalSortDirection = key === "updatedAt" ? "desc" : "asc";
      }
      renderLoadSongList(state.loadModalSongs);
    });
  });
  window.addEventListener("keydown", handleKeyDown);
}

function handleKeyDown(event) {
  const activeTag = document.activeElement?.tagName;
  if (["INPUT", "TEXTAREA", "SELECT"].includes(activeTag) || document.activeElement?.isContentEditable) {
    return;
  }
  if (event.key === "Backspace" || event.key === "Delete") {
    event.preventDefault();
    removeSelected();
  } else if (event.code === "Space") {
    event.preventDefault();
    togglePlayback();
  } else if (event.key === "Home") {
    const firstEvent = chordEvents()[0];
    if (firstEvent) {
      event.preventDefault();
      state.selectedEventId = firstEvent.id;
      state.paletteAppendMode = false;
      saveAppState();
      renderProgression();
      auditionChord(firstEvent);
    }
  }
}

wireStaticOptions();
loadStoredState();
wireEvents();
render();
